import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { useTemplateStore } from '../store/templateStore';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { EntryTemplate, TemplateCreate, categoriesAPI, Category, subprojectsAPI, ProjectSubproject } from '../services/api';
import { useTimezoneStore } from '../store/timezoneStore';
import { COMMON_ZONES, zoneLabel } from '../utils/timezone';

interface TemplateManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TemplateManager: React.FC<TemplateManagerProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    templates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    fetchTemplates,
    isLoading,
  } = useTemplateStore();
  const { projects } = useProjectStore();
  const { clients } = useClientStore();
  const { currentZone } = useTimezoneStore();

  const [categories, setCategories] = useState<Category[]>([]);
  const [subprojects, setSubprojects] = useState<ProjectSubproject[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EntryTemplate | null>(null);
  const [formData, setFormData] = useState<TemplateCreate & { start_time?: string }>({
    name: '',
    project_id: '',
    category_id: undefined,
    description: '',
    default_hours: undefined,
    is_favorite: false,
    start_time: '',
    start_time_tz: currentZone,
    subproject_id: '',
  });
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<EntryTemplate | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, fetchTemplates]);

  // Fetch categories and subprojects when selected project changes in form
  useEffect(() => {
    if (formData.project_id) {
      categoriesAPI.list(formData.project_id).then(setCategories).catch(() => setCategories([]));
      subprojectsAPI.list(formData.project_id).then(setSubprojects).catch(() => setSubprojects([]));
    } else {
      setCategories([]);
      setSubprojects([]);
    }
  }, [formData.project_id]);

  const handleOpenAdd = () => {
    setEditingTemplate(null);
    // Auto-select project if only one active project exists
    const activeProjects = projects.filter(p => p.is_active);
    const autoProjectId = activeProjects.length === 1 ? activeProjects[0].id : '';
    setFormData({
      name: '',
      project_id: autoProjectId,
      category_id: undefined,
      description: '',
      default_hours: undefined,
      is_favorite: false,
      start_time: '',
      start_time_tz: currentZone,
      subproject_id: '',
    });
    setFormError('');
    setIsAddModalOpen(true);
  };

  const handleOpenEdit = (template: EntryTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      project_id: template.project_id,
      category_id: template.category_id,
      description: template.description || '',
      default_hours: template.default_hours,
      is_favorite: template.is_favorite,
      start_time: template.start_time || '',
      start_time_tz: template.start_time_tz || currentZone,
      subproject_id: template.subproject_id || '',
    });
    setFormError('');
    setIsAddModalOpen(true);
  };

  const handleCloseAdd = () => {
    setIsAddModalOpen(false);
    setEditingTemplate(null);
    setFormData({
      name: '',
      project_id: '',
      category_id: undefined,
      description: '',
      default_hours: undefined,
      is_favorite: false,
      start_time: '',
      start_time_tz: currentZone,
      subproject_id: '',
    });
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setFormError('Template name is required');
      return;
    }
    if (!formData.project_id) {
      setFormError('Please select a project');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        name: formData.name.trim(),
        description: formData.description?.trim() || undefined,
        start_time: formData.start_time || undefined,
        // Anchor the template's time to the chosen zone (only meaningful with a time)
        start_time_tz: formData.start_time ? (formData.start_time_tz || currentZone) : undefined,
        subproject_id: formData.subproject_id || undefined,
      };

      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, submitData);
      } else {
        await createTemplate(submitData);
      }
      handleCloseAdd();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to save template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleFavorite = async (template: EntryTemplate) => {
    try {
      await updateTemplate(template.id, { is_favorite: !template.is_favorite });
    } catch (err) {
      // Error handled by store
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    setIsSubmitting(true);
    try {
      await deleteTemplate(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (err: any) {
      setFormError(err.response?.data?.detail || 'Failed to delete template');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group projects by client
  const groupedProjects = clients.map(client => ({
    client,
    projects: projects.filter(p => p.client_id === client.id && p.is_active),
  })).filter(group => group.projects.length > 0);

  const activeCategories = categories.filter(c => c.is_active);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Manage Templates" size="lg">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Create templates for quick time entry creation.
            </p>
            <Button onClick={handleOpenAdd} size="sm">
              <PlusIcon className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
            {templates.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No templates found. Create one to quickly add time entries.
              </div>
            ) : (
              templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleFavorite(template)}
                        className="flex-shrink-0"
                      >
                        {template.is_favorite ? (
                          <StarIconSolid className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <StarIcon className="h-4 w-4 text-gray-400 hover:text-yellow-500" />
                        )}
                      </button>
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {template.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {template.client_name} / {template.project_name}
                        {template.subproject_name && ` / ${template.subproject_name}`}
                      </span>
                      {template.category_name && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs"
                          style={{
                            backgroundColor: `${template.category_color}20`,
                            color: template.category_color,
                          }}
                        >
                          {template.category_name}
                        </span>
                      )}
                      {template.default_hours && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {template.default_hours}h
                        </span>
                      )}
                      {template.start_time && (
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          @ {template.start_time}{template.start_time_tz ? ` ${zoneLabel(template.start_time_tz)}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleOpenEdit(template)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(template)}
                      className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseAdd}
        title={editingTemplate ? 'Edit Template' : 'Add Template'}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Template Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Daily Standup"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Project
            </label>
            <select
              value={formData.project_id}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value, category_id: undefined, subproject_id: '' })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select a project...</option>
              {groupedProjects.map((group) => (
                <optgroup key={group.client.id} label={group.client.name}>
                  {group.projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category (optional)
            </label>
            <select
              value={formData.category_id || ''}
              onChange={(e) => setFormData({ ...formData, category_id: e.target.value || undefined })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">Select a category...</option>
              {activeCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {formData.project_id && subprojects.filter(s => s.is_active).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Subproject <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                value={formData.subproject_id || ''}
                onChange={(e) => setFormData({ ...formData, subproject_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">No subproject</option>
                {subprojects.filter(s => s.is_active).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Description (optional)
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What will you typically work on?"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Hours (optional)
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max="24"
              value={formData.default_hours || ''}
              onChange={(e) => setFormData({ ...formData, default_hours: e.target.value ? parseFloat(e.target.value) : undefined })}
              placeholder="e.g., 0.5"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Start Time (optional)
            </label>
            <input
              type="time"
              value={formData.start_time || ''}
              onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            {formData.start_time && (
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Time zone this meeting time is anchored to
                </label>
                <select
                  value={formData.start_time_tz || currentZone}
                  onChange={(e) => setFormData({ ...formData, start_time_tz: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                >
                  {!COMMON_ZONES.some((z) => z.id === (formData.start_time_tz || currentZone)) && (
                    <option value={formData.start_time_tz || currentZone}>
                      {zoneLabel(formData.start_time_tz || currentZone)}
                    </option>
                  )}
                  {COMMON_ZONES.map((z) => (
                    <option key={z.id} value={z.id}>
                      {zoneLabel(z.id)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Applied in another zone, this time converts to your local time
                  (e.g. 8:30 Mountain becomes 10:30 Eastern).
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_favorite"
              checked={formData.is_favorite}
              onChange={(e) => setFormData({ ...formData, is_favorite: e.target.checked })}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label htmlFor="is_favorite" className="text-sm text-gray-700 dark:text-gray-300">
              Mark as favorite (appears at top of Quick Add)
            </label>
          </div>

          {formError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={handleCloseAdd} type="button">
              Cancel
            </Button>
            <Button type="submit" isLoading={isSubmitting}>
              {editingTemplate ? 'Save' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Template"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete the template "{deleteConfirm?.name}"?
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isSubmitting}>
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
